import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  UnlockedAchievement,
  AchievementTemplate,
  AchievementProgress,
  AchievementStats,
  CheckAchievementsRequest,
  GetAchievementsRequest,
  AchievementTrigger,
  AchievementCategory,
  AchievementRarity
} from '../../types/achievement';
import { Achievement } from '../../types/database';
import { AchievementAPI, AchievementHelpers } from '../../lib/AchievementSystem';

// Async thunks
export const checkAchievements = createAsyncThunk(
  'achievements/check',
  async (request: CheckAchievementsRequest) => {
    const response = await AchievementAPI.checkAchievements(request);
    if (!response.success) {
      throw new Error(response.error || 'Failed to check achievements');
    }
    return {
      new_achievements: response.new_achievements || [],
      points_awarded: response.points_awarded || 0
    };
  }
);

export const fetchAchievements = createAsyncThunk(
  'achievements/fetch',
  async (request: GetAchievementsRequest) => {
    const response = await AchievementAPI.getAchievements(request);
    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch achievements');
    }
    return {
      unlocked_achievements: response.unlocked_achievements || [],
      locked_achievements: response.locked_achievements || [],
      achievement_progress: response.achievement_progress || [],
      stats: response.stats || null
    };
  }
);

export const checkAchievementsOnExpenseAdd = createAsyncThunk(
  'achievements/checkOnExpenseAdd',
  async (params: {
    couple_id: string;
    expense_amount: number;
    expense_category_id: string;
    expense_id: string;
  }) => {
    const request: CheckAchievementsRequest = {
      couple_id: params.couple_id,
      trigger_type: AchievementTrigger.EXPENSE_ADDED,
      trigger_data: {
        expense_amount: params.expense_amount,
        expense_category_id: params.expense_category_id,
        expense_id: params.expense_id
      }
    };

    const response = await AchievementAPI.checkAchievements(request);
    if (!response.success) {
      throw new Error(response.error || 'Failed to check achievements');
    }
    
    return {
      new_achievements: response.new_achievements || [],
      points_awarded: response.points_awarded || 0
    };
  }
);

export const checkAchievementsOnChallengeComplete = createAsyncThunk(
  'achievements/checkOnChallengeComplete',
  async (params: {
    couple_id: string;
    challenge_id: string;
    challenge_type: string;
  }) => {
    const request: CheckAchievementsRequest = {
      couple_id: params.couple_id,
      trigger_type: AchievementTrigger.CHALLENGE_COMPLETED,
      trigger_data: {
        challenge_id: params.challenge_id,
        challenge_type: params.challenge_type
      }
    };

    const response = await AchievementAPI.checkAchievements(request);
    if (!response.success) {
      throw new Error(response.error || 'Failed to check achievements');
    }
    
    return {
      new_achievements: response.new_achievements || [],
      points_awarded: response.points_awarded || 0
    };
  }
);

interface AchievementState {
  // Achievement data
  unlockedAchievements: UnlockedAchievement[];
  lockedAchievements: AchievementTemplate[];
  achievementProgress: AchievementProgress[];
  
  // Statistics
  stats: AchievementStats | null;
  
  // Loading states
  loading: {
    fetching: boolean;
    checking: boolean;
  };
  
  // Error states
  errors: {
    fetch: string | null;
    check: string | null;
  };
  
  // UI state
  filters: {
    category: AchievementCategory | 'all';
    rarity: AchievementRarity | 'all';
    showLocked: boolean;
  };
  selectedAchievement: UnlockedAchievement | null;
  
  // Notifications
  recentUnlocks: Achievement[];
  totalPointsAwarded: number;
  showNotifications: boolean;
  
  // Gallery view state
  viewMode: 'grid' | 'list';
  sortBy: 'date' | 'rarity' | 'category' | 'points';
  sortDirection: 'asc' | 'desc';
}

const initialState: AchievementState = {
  unlockedAchievements: [],
  lockedAchievements: [],
  achievementProgress: [],
  
  stats: null,
  
  loading: {
    fetching: false,
    checking: false,
  },
  
  errors: {
    fetch: null,
    check: null,
  },
  
  filters: {
    category: 'all',
    rarity: 'all',
    showLocked: true,
  },
  selectedAchievement: null,
  
  recentUnlocks: [],
  totalPointsAwarded: 0,
  showNotifications: false,
  
  viewMode: 'grid',
  sortBy: 'date',
  sortDirection: 'desc',
};

const achievementSlice = createSlice({
  name: 'achievements',
  initialState,
  reducers: {
    // Clear errors
    clearErrors: (state) => {
      state.errors = {
        fetch: null,
        check: null,
      };
    },
    
    // Clear specific error
    clearError: (state, action: PayloadAction<keyof AchievementState['errors']>) => {
      state.errors[action.payload] = null;
    },
    
    // Update filters
    setFilters: (state, action: PayloadAction<Partial<AchievementState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    
    // Clear filters
    clearFilters: (state) => {
      state.filters = {
        category: 'all',
        rarity: 'all',
        showLocked: true,
      };
    },
    
    // Set selected achievement
    setSelectedAchievement: (state, action: PayloadAction<UnlockedAchievement | null>) => {
      state.selectedAchievement = action.payload;
    },
    
    // Update view mode
    setViewMode: (state, action: PayloadAction<'grid' | 'list'>) => {
      state.viewMode = action.payload;
    },
    
    // Update sort settings
    setSortBy: (state, action: PayloadAction<AchievementState['sortBy']>) => {
      // If clicking the same sort field, toggle direction
      if (state.sortBy === action.payload) {
        state.sortDirection = state.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        state.sortBy = action.payload;
        state.sortDirection = 'desc'; // Default to descending for new sort
      }
    },
    
    // Clear notifications
    clearNotifications: (state) => {
      state.recentUnlocks = [];
      state.totalPointsAwarded = 0;
      state.showNotifications = false;
    },
    
    // Set show notifications
    setShowNotifications: (state, action: PayloadAction<boolean>) => {
      state.showNotifications = action.payload;
    },
    
    // Add new achievement (for real-time updates)
    addUnlockedAchievement: (state, action: PayloadAction<Achievement>) => {
      const formattedAchievement = AchievementHelpers.formatAchievementForDisplay(action.payload);
      state.unlockedAchievements.unshift(formattedAchievement);
      
      // Remove from locked achievements if it was there
      state.lockedAchievements = state.lockedAchievements.filter(
        template => template.id !== action.payload.id
      );
      
      // Update progress for this achievement
      const progressIndex = state.achievementProgress.findIndex(
        p => p.template_id === action.payload.id
      );
      if (progressIndex !== -1) {
        state.achievementProgress[progressIndex].is_unlocked = true;
        state.achievementProgress[progressIndex].progress_percentage = 100;
      }
      
      // Add to recent unlocks for notifications
      state.recentUnlocks.push(action.payload);
      state.totalPointsAwarded += action.payload.points_awarded;
      state.showNotifications = true;
    },
    
    // Update achievement progress (for real-time progress updates)
    updateAchievementProgress: (state, action: PayloadAction<AchievementProgress[]>) => {
      state.achievementProgress = action.payload;
    },
    
    // Reset achievement state
    resetAchievementState: (state) => {
      return { ...initialState };
    },
  },
  extraReducers: (builder) => {
    // Check achievements
    builder
      .addCase(checkAchievements.pending, (state) => {
        state.loading.checking = true;
        state.errors.check = null;
      })
      .addCase(checkAchievements.fulfilled, (state, action) => {
        state.loading.checking = false;
        
        const { new_achievements, points_awarded } = action.payload;
        
        if (new_achievements.length > 0) {
          // Add new achievements to unlocked list
          const formattedAchievements = new_achievements.map(achievement =>
            AchievementHelpers.formatAchievementForDisplay(achievement)
          );
          state.unlockedAchievements.unshift(...formattedAchievements);
          
          // Update progress for unlocked achievements
          new_achievements.forEach(achievement => {
            const progressIndex = state.achievementProgress.findIndex(
              p => p.template_id === achievement.id
            );
            if (progressIndex !== -1) {
              state.achievementProgress[progressIndex].is_unlocked = true;
              state.achievementProgress[progressIndex].progress_percentage = 100;
            }
          });
          
          // Set up notifications
          state.recentUnlocks = new_achievements;
          state.totalPointsAwarded = points_awarded;
          state.showNotifications = true;
        }
      })
      .addCase(checkAchievements.rejected, (state, action) => {
        state.loading.checking = false;
        state.errors.check = action.error.message || 'Failed to check achievements';
      });
    
    // Check achievements on expense add
    builder
      .addCase(checkAchievementsOnExpenseAdd.pending, (state) => {
        state.loading.checking = true;
        state.errors.check = null;
      })
      .addCase(checkAchievementsOnExpenseAdd.fulfilled, (state, action) => {
        state.loading.checking = false;
        
        const { new_achievements, points_awarded } = action.payload;
        
        if (new_achievements.length > 0) {
          const formattedAchievements = new_achievements.map(achievement =>
            AchievementHelpers.formatAchievementForDisplay(achievement)
          );
          state.unlockedAchievements.unshift(...formattedAchievements);
          
          state.recentUnlocks = new_achievements;
          state.totalPointsAwarded = points_awarded;
          state.showNotifications = true;
        }
      })
      .addCase(checkAchievementsOnExpenseAdd.rejected, (state, action) => {
        state.loading.checking = false;
        state.errors.check = action.error.message || 'Failed to check achievements';
      });
    
    // Check achievements on challenge complete
    builder
      .addCase(checkAchievementsOnChallengeComplete.pending, (state) => {
        state.loading.checking = true;
        state.errors.check = null;
      })
      .addCase(checkAchievementsOnChallengeComplete.fulfilled, (state, action) => {
        state.loading.checking = false;
        
        const { new_achievements, points_awarded } = action.payload;
        
        if (new_achievements.length > 0) {
          const formattedAchievements = new_achievements.map(achievement =>
            AchievementHelpers.formatAchievementForDisplay(achievement)
          );
          state.unlockedAchievements.unshift(...formattedAchievements);
          
          state.recentUnlocks = new_achievements;
          state.totalPointsAwarded = points_awarded;
          state.showNotifications = true;
        }
      })
      .addCase(checkAchievementsOnChallengeComplete.rejected, (state, action) => {
        state.loading.checking = false;
        state.errors.check = action.error.message || 'Failed to check achievements';
      });
    
    // Fetch achievements
    builder
      .addCase(fetchAchievements.pending, (state) => {
        state.loading.fetching = true;
        state.errors.fetch = null;
      })
      .addCase(fetchAchievements.fulfilled, (state, action) => {
        state.loading.fetching = false;
        
        const { unlocked_achievements, locked_achievements, achievement_progress, stats } = action.payload;
        
        state.unlockedAchievements = unlocked_achievements;
        state.lockedAchievements = locked_achievements;
        state.achievementProgress = achievement_progress;
        state.stats = stats;
      })
      .addCase(fetchAchievements.rejected, (state, action) => {
        state.loading.fetching = false;
        state.errors.fetch = action.error.message || 'Failed to fetch achievements';
      });
  },
});

export const {
  clearErrors,
  clearError,
  setFilters,
  clearFilters,
  setSelectedAchievement,
  setViewMode,
  setSortBy,
  clearNotifications,
  setShowNotifications,
  addUnlockedAchievement,
  updateAchievementProgress,
  resetAchievementState,
} = achievementSlice.actions;

export default achievementSlice.reducer;